import * as React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { Button, RadioGroup, Modal, ModalContent, ModalConfirmContent } from '@restworld/ui-ds-base';
import { cn, useModal } from '@restworld/utils-common';

// The withdraw CTA is a solid-red button (matches production's `danger-with-bg`). ui-ds-base has
// no solid-danger variant, so we take `secondary-danger` and force the solid background + white
// text via className (twMerge wins over the variant's tinted bg). The DS icon-color map would
// tint the icon red — invisible on the red bg — so force it white here.
const WhiteTrashIcon = (props: React.ComponentProps<'svg'>) => (
  <TrashIcon {...props} className={cn(props.className, 'text-white')} />
);

import {
  getWorkerDiscardedReasonVariableAtom,
  setSelectedDiscardedReasonAtom,
  getSelectedDiscardedReasonAtom,
  setDiscardedOtherReasonAtom,
  getDiscardedOtherReasonAtom,
  getApplicationDiscardSuccessAtom,
  applicationDiscardLoadingAtom,
  getApplicationStateAtom,
  getApplyJobStatusDataAtom,
} from '../application-details.context';
import { useWorkerDiscardedReasonVariable, useDiscardApplicationHandlers } from '../application-details.hooks';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IApplicationDiscardButtonProps {}

export const ApplicationDiscardButton: React.FunctionComponent<IApplicationDiscardButtonProps> = (props) => {
  const { show: showDiscardModal, close: hideDiscardModal, open: openDiscardModal } = useModal();
  const [applicationDiscardSuccess] = useAtom(getApplicationDiscardSuccessAtom);
  const applicationState = useAtomValue(getApplicationStateAtom);
  const jaData = useAtomValue(getApplyJobStatusDataAtom);
  const { t } = useTranslation();
  useWorkerDiscardedReasonVariable();
  return (
    <div className="mx-auto">
      {!['rejected', 'accepted', 'not-interested'].includes(applicationState) &&
      !jaData?.job_position?.published_from_ats ? (
        <Button
          variant="secondary-danger"
          className="bg-red-700 text-white"
          StartIcon={WhiteTrashIcon}
          onClick={() => {
            openDiscardModal();
          }}
        >
          {t('job-application-page:withdraw_application')}
        </Button>
      ) : null}
      <Modal open={showDiscardModal} onOpenChange={(v) => !v && hideDiscardModal()}>
        <ModalContent
          className="w-full md:max-w-[26rem] md:min-w-[22rem]"
          // On the form state, pin the submit in the fixed footer so it stays visible while the
          // reason list scrolls. The success state uses ModalConfirmContent's own primary action.
          footer={applicationDiscardSuccess ? undefined : <ApplicationDiscardSubmit />}
        >
          {applicationDiscardSuccess ? (
            <ModalConfirmContent
              icon={<CheckCircleIcon className="h-10 w-10 text-green-500" />}
              title={t('job-application-page:application_withdrawn')}
              subtitle={t('job-application-page:you_can_reapply')}
              primaryAction={{
                label: t('common:close'),
                onClick() {
                  hideDiscardModal();
                },
              }}
            />
          ) : (
            <ApplicationDiscardForm />
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ApplicationDiscardButton;
const ApplicationDiscardForm = () => {
  const [workerDiscardedReasonVariable] = useAtom(getWorkerDiscardedReasonVariableAtom);
  const [, setSelectedDiscardedReason] = useAtom(setSelectedDiscardedReasonAtom);
  const [selectedDiscardedReason] = useAtom(getSelectedDiscardedReasonAtom);
  const [discardedOtherReason] = useAtom(getDiscardedOtherReasonAtom);
  const [, setDiscardedOtherReason] = useAtom(setDiscardedOtherReasonAtom);
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-6 flex-col">
      <div className="flex gap-4 flex-col">
        <div className="text-center text-gray-900 text-2xl font-semibold leading-9">
          {t('job-application-page:why_withdraw_your_application')} 💔
        </div>
        <div className="text-center text-gray-600 text-base font-normal leading-normal">
          {t('job-application-page:no_longer_interested_but_they_will_not_know_why')}🤐
        </div>
      </div>
      <RadioGroup
        id="workerDiscardedReasonVariable"
        options={[
          ...(workerDiscardedReasonVariable ?? []),
          ...[
            {
              id: 'other',
              key: 'other',
              parent_key: 'worker',
              label: t('common:other'),
            },
          ],
        ]}
        returnKey="key"
        value={selectedDiscardedReason}
        className={'px-2 max-h-104 overflow-y-auto'}
        onChange={(value) => {
          setSelectedDiscardedReason(value);
        }}
        inlineTextInputProps={{
          placeholder: t('job-application-page:write_here_the_reason'),
          id: 'others',
          value: discardedOtherReason ?? '',
          onChange(value) {
            setDiscardedOtherReason(value);
          },
        }}
      />
    </div>
  );
};

// Submit CTA for the discard form, rendered in the modal's fixed footer (not inside the scrollable
// body). Reads its state from atoms/handlers so it can live outside the form's render tree.
const ApplicationDiscardSubmit = () => {
  const selectedDiscardedReason = useAtomValue(getSelectedDiscardedReasonAtom);
  const applicationDiscardLoading = useAtomValue(applicationDiscardLoadingAtom);
  const { updateDiscardApplication } = useDiscardApplicationHandlers();
  const { t } = useTranslation();
  return (
    <Button
      disabled={!selectedDiscardedReason}
      loading={applicationDiscardLoading}
      variant="secondary-danger"
      className="bg-red-700 text-white"
      StartIcon={WhiteTrashIcon}
      onClick={updateDiscardApplication}
      fullwidth
    >
      {t('job-application-page:withdraw_application')}
    </Button>
  );
};
